<template>
    <b-container>
        <h2>Instagram Account Coach<span v-if="instagramName"> for <a :href="'https://instagram.com/' + instagramName" target="_blank">@{{instagramName}}</a></span></h2>
        <p>
            <strong>Report based on: &nbsp;&nbsp;</strong>
            <font-awesome-icon :icon="['far', 'calendar']"/>  {{startAt | formatDate}} - {{endAt | formatDate}},&nbsp;&nbsp;
            <font-awesome-icon :icon="['far', 'user']"/> {{followers}} followers,&nbsp;&nbsp;
            <font-awesome-icon :icon="['far', 'image']"/>  {{postsCount}} posts,&nbsp;&nbsp;
            <font-awesome-icon :icon="['far', 'comment']"/> {{commentsCount}} comments
        </p>
        <DataPanel
                title="All Posts"
                :class="{locked: locked, 'loading-animation': loaded}"
                type="all-posts"
                :links="[]"
                :account="instagramName"
                :uuid="uuid"
                :count="postsCount"
                :download="download"
                >
            <b-table responsive :items="table" class="fading heading-no-wrap" small>
                <template slot="timestamp" slot-scope="data">
                    {{data.value}}
                </template>
                <template slot="imageObjects" slot-scope="data">
                    {{data.value.slice(0, 2).join(',&nbsp;')}}<span v-if="data.value.length > 2">, ...</span>
                </template>
                <template slot="hashtagsInCaption" slot-scope="data">
                    {{data.value.slice(0, 2).map(x=> '#'+x).join(',&nbsp;')}}<span v-if="data.value.length > 2">, ...</span>
                </template>
                <template slot="hashtagsInCommentsByOwner" slot-scope="data">
                    {{data.value.slice(0, 2).map(x=> '#'+x).join(',&nbsp;')}}<span v-if="data.value.length > 2">, ...</span>
                </template>
                <template slot="hashtagsInCommentsByOthers" slot-scope="data">
                    {{data.value.slice(0, 2).map(x=> '#'+x).join(',&nbsp;')}}<span v-if="data.value.length > 2">, ...</span>
                </template>
                <template slot="colorPalette" slot-scope="data">
                    {{data.value.slice(0, 2).map(x=> '#' + x[0].toString(16) + x[1].toString(16) + x[2].toString(16)).join(',&nbsp;')}}<span v-if="data.value.length > 2">, ...</span>
                </template>
            </b-table>
        </DataPanel>

        <DataPanel
                title="All commenters"
                :class="{locked: locked, 'loading-animation': loaded}"
                type="all-comments"
                :links="[]"
                :account="instagramName"
                :uuid="uuid"
                :count="commentsCount"
                :download="download">
            <b-table responsive :items="tableCommenters" class="fading heading-no-wrap" small>
                <template slot="profile_picture" slot-scope="data">
                    <img class="profile-picture" :src="data.value">
                </template>
            </b-table>
        </DataPanel>

        <DataPanel
                title="Number of posts"
                :bigNumber="postsCount"
                :trend="trendPostingActivity"
                takeAway="When you see a big change in other charts come back to this chart to rule out change in activity as the cause."
                :class="{locked: locked, 'loading-animation': loaded}"
                type="post-activity"
                :links="[]"
                :account="instagramName"
                :uuid="uuid"
                :count="activity.length"
                :download="download">
            <TotalBarChart
                    :data="activity"
                    label="Activity"
                    background-color="#cc528f"
                    border-color="#cc528f"
                    :allow-decimals="false"
                />
        </DataPanel>

        <h2 class="mt-5">Impressions</h2>
        <div>Impressions indicate how many times your posts were seen. </div>
        <div class="data-panel-grid">
            <DataPanel
                    title="Total Impressions"
                    :bigNumber="impressions.reduce((r, i) => r + i[1], 0) | round(precision)"
                    :trend="growsTotalImpressions"
                    takeAway="Post more often, get more followers or use better hashtags to improve it."
                    :class="{locked: locked, 'loading-animation': loaded}"
                    type="total-impressions"
                    :links="[]"
                    :account="instagramName"
                    :uuid="uuid"
                    :count="impressions.length"
                    :download="download">
                <TotalBarChart
                    :data="impressions"
                    label="Impressions"
                    background-color="#52cacc"
                    border-color="#52cacc"
                    />
            </DataPanel>
            <DataPanel
                    title="Average Impressions per Post"
                    :bigNumber="avgImpressions | round(precision)"
                    :trend="growsAverageImpressions"
                    takeAway="This reflects the effectiveness of your hashtags and follower growth. Frequency of posting does not influence this metric."
                    :class="{locked: locked, 'loading-animation': loaded}"
                    type="average-impressions-per-post"
                    :links="[]"
                    :account="instagramName"
                    :uuid="uuid"
                    :count="impressions.length"
                    :download="download">
                <AverageBarChart
                    :data="impressions"
                    label="Average Impressions"
                    background-color="#52cacc"
                    border-color="#52cacc"
                    />
            </DataPanel>
        </div>
        <h2 class="mt-5">Hashtags</h2>
        <div>Adding hashtags makes your posts discoverable even for people who do not follow you.</div>
        <div class="data-panel-grid">
            <DataPanel
                    title="Most Effective Hashtags"
                    takeAway="Find out which hashtags increase your impressions the most. Make sure you keep using them."
                    :class="{locked: locked, 'loading-animation': loaded}"
                    type="most-effective-hashtags"
                    :links="[]"
                    :account="instagramName"
                    :uuid="uuid"
                    :count="hashtags.length"
                    :download="download">
                <b-table :items="hashtags" borderless small>
                    <template slot="HEAD_used" slot-scope="data">
                        <div class="text-right">Used</div>
                    </template>
                    <template slot="HEAD_impressions" slot-scope="data">
                        <div class="text-right">Impressions</div>
                    </template>
                    <template slot="hashtag" slot-scope="data">
                        <BookmarkButton :hashtag="data.value"/>
                        &nbsp;#{{ data.value }}
                    </template>
                    <template slot="used" slot-scope="data">
                        <div class="text-right">{{ data.value }}x</div>
                    </template>
                    <template slot="impressions" slot-scope="data">
                        <div class="text-right">
                            <strong>
                                <span v-if="data.value >= 0" class="text-success">+{{ data.value }}%</span>
                                <span v-else class="text-danger">+{{ data.value }}%</span>
                            </strong>
                        </div>
                    </template>
                </b-table>
            </DataPanel>
            <DataPanel
                    title="New hashtags to try"
                    takeAway="RiteTag found the above hashtags related to the photos you shared that you have not used yet. Make sure you try them in the future. Use RiteTag <a href='https://itunes.apple.com/cz/app/ritetag/id1151267309?mt=8' target='_blank'>iOS</a> or the <a href='https://play.google.com/store/apps/details?id=com.ritekit.ritetag' target='_blank'>Android</a> app to get push notifications with hashtag suggestions generated specifically for your newly-posted photos."
                    :class="{locked: locked, 'loading-animation': loaded}"
                    type="new-hashtags"
                    :links="[]"
                    :account="instagramName"
                    :uuid="uuid"
                    :count="newHashtags.length"
                    :download="download">
                <b-table :items="newHashtags" thead-class="hidden_header" borderless small>
                    <template slot="hashtag" slot-scope="data">
                        <BookmarkButton :hashtag="data.value"/>
                        &nbsp;#{{ data.value }}
                    </template>
                    <template slot="used" slot-scope="data">
                        {{ data.value }}x
                    </template>
                    <template slot="impressions" slot-scope="data">
                        <strong>
                            <span v-if="data.value >= 0" class="text-success">+{{ data.value }}%</span>
                            <span v-else class="text-danger">+{{ data.value }}%</span>
                        </strong>
                    </template>
                </b-table>
            </DataPanel>
        </div>
        <div class="data-panel-grid">
            <DataPanel
                    title="Hashtags Count Effect on Impressions"
                    takeAway="Check if using more hashtags leads to more impressions. If not, try using different hashtags."
                    :class="{locked: locked, 'loading-animation': loaded}"
                    type="hashtag-count-effect-on-impressions"
                    :links="[]"
                    :account="instagramName"
                    :uuid="uuid"
                    :count="hashtagCountEffectOnImpressions.length"
                    :download="download">
                <AverageBarChart
                        :data="hashtagCountEffectOnImpressions"
                        label="Avg. Impressions"
                        background-color="#7f52cc"
                        border-color="#7f52cc"
                />
            </DataPanel>
            <DataPanel
                    title="Hashtags Count Effect on Engagement"
                    takeAway="Using a lot of hashtags increases impressions but it may lower the engagement. The chart above shows optimal number of hashtags to use."
                    :class="{locked: locked, 'loading-animation': loaded}"
                    type="hashtag-count-effect-on-engagement"
                    :links="[]"
                    :account="instagramName"
                    :uuid="uuid"
                    :count="hashtagCountEffectOnEngagement.length"
                    :download="download">
                <AverageBarChart
                        :data="hashtagCountEffectOnEngagement"
                        label="Avg. Engagement"
                        background-color="#5281cc"
                        border-color="#5281cc"
                />
            </DataPanel>
        </div>
        <h2 class="mt-5">Engagement</h2>
        <div>Engagement indicates how many likes and comments your posts get.</div>
        <div class="data-panel-grid">
            <DataPanel
                    title="Total Engagement"
                    :bigNumber="engagements.reduce((r, i) => r + i[1], 0) | round(precision)"
                    takeAway="You can improve total engagement simply by getting more people to see your posts (see suggestions in the Impressions section above) or by increasing the attractiveness of your posts."
                    :trend="growsTotalEngagement"
                    :class="{locked: locked, 'loading-animation': loaded}"
                    type="total-engagement"
                    :links="[]"
                    :account="instagramName"
                    :uuid="uuid"
                    :count="engagements.length"
                    :download="download">
                <TotalBarChart
                        :data="engagements"
                        label="Engagements"
                        background-color="#f0b237"
                        border-color="#f0b237"
                />
            </DataPanel>

            <DataPanel
                    title="Average Engagement per Post"
                    :bigNumber="avgEngagement | round(precision)"
                    :trend="growsAverageEngagement"
                    takeAway="To improve it, learn what your audience likes and create more posts like that. You can find detailed suggestions in the sections below."
                    :class="{locked: locked, 'loading-animation': loaded}"
                    type="average-engagement-per-post"
                    :links="[]"
                    :account="instagramName"
                    :uuid="uuid"
                    :count="engagements.length"
                    :download="download">
                <AverageBarChart
                        :data="engagements"
                        label="Avg. Engagement"
                        background-color="#f0b237"
                        border-color="#f0b237"
                />
            </DataPanel>
        </div>
        <DataPanel
                title="Most Engaging Content of the Photos"
                :class="{locked: locked, 'loading-animation': loaded}"
                type="most-engaging-content-of-the-photos"
                :links="[]"
                :account="instagramName"
                :uuid="uuid"
                :count="mostEngagingContent.length"
                :download="download">
            <div class="carousel-grid">
                <div v-for="content in mostEngagingContent" v-bind:key="content.object">
                    <b-carousel
                        :interval="20000"
                        controls
                        indicators
                        img-width="200"
                        img-height="200"
                        >
                        <b-carousel-slide v-for="i in content.images" v-bind:key="i" :img-src="i"></b-carousel-slide>
                    </b-carousel>
                    <div class="number" :class="{'text-success': content.trend > 0, 'text-danger': content.trend < 0}">
                        <span v-if="content.posted == 0">No data</span>
                        <span v-else>
                            <span v-if="content.trend >= 0">+</span>{{content.trend}}%
                        </span>
                    </div>
                    <p>
                        <span v-if="content.posted == 0">for</span>
                        <span v-else-if="content.trend >= 0">more</span>
                        <span v-else-if="content.trend < 0">less</span> engagement of posts with <br>
                        <strong>{{content.object}}</strong>
                    </p>
                </div>
            </div>
        </DataPanel>
        <DataPanel
                title="Most engaging times to post"
                take-away="Learn which times of the day and days of the week tend to have better engagement (all times are in UTC)."
                :class="{locked: locked, 'loading-animation': loaded}"
                type="most-engaging-times-to-post"
                :links="[]"
                :account="instagramName"
                :uuid="uuid"
                :count="mostEngagingTimesToPostCount"
                :download="download">
            <Heatmap :data="mostEngagingTimesToPost"/>
        </DataPanel>
        <div class="data-panel-grid">
            <DataPanel
                title="Engagement by colors in the photo"
                take-away="Do you see any correlation between certain color and engagement? If yes, try posting more photos that contain that color."
                :class="{locked: locked, 'loading-animation': loaded}"
                type="engagement-by-colors-in-the-photo"
                :links="[]"
                :account="instagramName"
                :uuid="uuid"
                :count="engagementByColors.length"
                :download="download">
                <DominantColor :data="engagementByColors"/>
            </DataPanel>
            <DataPanel
                title="Engagement by Media Type"
                takeAway="Look for big differences between quantity and engagement for each media type. Maybe videos have disproportionately more engagement? Or maybe not, and it is not worth investing too much effort into shooting them?"
                :class="{locked: locked, 'loading-animation': loaded}"
                type="engagement-by-media-type"
                :links="[]"
                :account="instagramName"
                :uuid="uuid"
                :count="3"
                :download="download">
                <b-table :items="engagementByType" borderless striped>
                    <template slot="HEAD_posted" slot-scope="data">
                        <div class="text-right">Posted</div>
                    </template>
                    <template slot="HEAD_engagement" slot-scope="data">
                        <div class="text-right">Avg. engagement per post</div>
                    </template>
                    <template slot="posted" slot-scope="data">
                        <div class="text-right">{{ data.value }}x</div>
                    </template>
                    <template slot="engagement" slot-scope="data">
                        <div class="text-right">
                            <strong>
                                <span v-if="data.value >= 0" class="text-success">{{ data.value }}</span>
                                <span v-else class="text-danger">+{{ data.value }}</span>
                            </strong>
                        </div>
                    </template>
                </b-table>
            </DataPanel>
        </div>
        <DataPanel
                title="Caption Analytics"
                :class="{locked: locked, 'loading-animation': loaded}"
                type="caption-analytics"
                :links="[]"
                :account="instagramName"
                :uuid="uuid"
                :count="3"
                :download="download">
            <div class="symbol-grid">
                <div>
                    <div class="symbol emoji"><font-awesome-icon :icon="['fa', 'smile']"/></div>
                    <div class="number" :class="{'text-success': mostEngagingCaptions.emoji.grow > 0, 'text-danger': mostEngagingCaptions.emoji.grow < 0}">
                        <span v-if="mostEngagingCaptions.emoji.posted == 0">No data</span>
                        <span v-else>
                            <span v-if="mostEngagingCaptions.emoji.grow >= 0">+</span>{{mostEngagingCaptions.emoji.grow}}%
                        </span>
                    </div>
                    <p>
                        <span v-if="mostEngagingCaptions.emoji.posted == 0">for</span>
                        <span v-else-if="mostEngagingCaptions.emoji.grow >= 0">more</span>
                        <span v-else-if="mostEngagingCaptions.emoji.grow < 0">less</span> engagement of posts with <br>
                        <strong>emojis in the caption</strong>
                    </p>
                </div>
                <div>
                    <div class="symbol hashtag"><font-awesome-icon :icon="['fa', 'hashtag']"/></div>
                    <div class="number" :class="{'text-success': mostEngagingCaptions.hashtag.grow > 0, 'text-danger': mostEngagingCaptions.hashtag.grow < 0}">
                        <span v-if="mostEngagingCaptions.hashtag.posted == 0">No data</span>
                        <span v-else>
                            <span v-if="mostEngagingCaptions.hashtag.grow >= 0">+</span>{{mostEngagingCaptions.hashtag.grow}}%
                        </span>
                    </div>
                    <p>
                        <span v-if="mostEngagingCaptions.hashtag.posted == 0">for</span>
                        <span v-else-if="mostEngagingCaptions.hashtag.grow >= 0">more</span>
                        <span v-else-if="mostEngagingCaptions.hashtag.grow < 0">less</span> engagement of posts with <br>
                        <strong>hashtags in the caption</strong>
                    </p>
                </div>
                <div>
                    <div class="symbol question"><font-awesome-icon :icon="['fa', 'question']"/></div>
                    <div class="number"  :class="{'text-success': mostEngagingCaptions.question.grow > 0, 'text-danger': mostEngagingCaptions.question.grow < 0}">
                        <span v-if="mostEngagingCaptions.question.posted == 0">No data</span>
                        <span v-else>
                            <span v-if="mostEngagingCaptions.question.grow >= 0">+</span>{{mostEngagingCaptions.question.grow}}%
                        </span>
                    </div>
                    <p>
                        <span v-if="mostEngagingCaptions.question.posted == 0">for</span>
                        <span v-else-if="mostEngagingCaptions.question.grow >= 0">more</span>
                        <span v-else-if="mostEngagingCaptions.question.grow < 0">less</span> engagement of posts with <br>
                        <strong>question in the caption</strong>
                    </p>
                </div>
            </div>
        </DataPanel>

        <h2 class="mt-5">Comments</h2>

        <div class="data-panel-grid">
            <DataPanel
                    title="Comments"
                    :trend="growsComments"
                    :class="{locked: locked, 'loading-animation': loaded}"
                    type="comments-chart"
                    :links="[]"
                    :account="instagramName"
                    :uuid="uuid"
                    :count="commentsChart.length"
                    :download="download">
                <TotalBarChart
                        :data="commentsChart"
                        label="Comments"
                        background-color="#cc528f"
                        border-color="#cc528f"
                        :allow-decimals="false"
                />
            </DataPanel>
            <DataPanel
                    title="Unique commenters"
                    :class="{locked: locked, 'loading-animation': loaded}"
                    type="unique-commenters"
                    :trend="growsCommenters"
                    :links="[]"
                    :account="instagramName"
                    :uuid="uuid"
                    :count="uniqueCommentersChart.length"
                    :download="download">
                <TotalBarChart
                        :data="uniqueCommentersChart"
                        label="Unique commenters"
                        background-color="#cc528f"
                        border-color="#cc528f"
                        :allow-decimals="false"
                />
            </DataPanel>
        </div>
        <div class="data-panel-grid">
            <DataPanel
                    title="Sentiment timeline"
                    :class="{locked: locked, 'loading-animation': loaded}"
                    type="sentiment-timeline"
                    :links="[]"
                    :account="instagramName"
                    :uuid="uuid"
                    :count="sentimentHistoryCount"
                    :download="download">
                <sentiment-history
                        :data="sentimentHistory"
                />
            </DataPanel>
            <DataPanel
                    title="Overall Comment Sentiment"
                    :class="{locked: locked, 'loading-animation': loaded}"
                    type="overall-comment-sentiment"
                    :links="[]"
                    :account="instagramName"
                    :uuid="uuid"
                    :count="commentsHistogram.filter(x => x[1] > 0).length"
                    :download="download">
                <histogram
                        :chartData="commentsHistogram"
                />
            </DataPanel>
        </div>
        <div class="data-panel-grid">
            <DataPanel
                    title="Most used positive adjectives"
                    :class="{locked: locked, 'loading-animation': loaded}"
                    type="positive-adjectives"
                    :links="[]"
                    :account="instagramName"
                    :uuid="uuid"
                    :count="positiveAdjectives.length"
                    :download="download">
                <vue-word-cloud
                        class="fixed-word-cloud"
                        :words="positiveAdjectives"
                        :color="color"
                        font-family="Lato"
                        :fontSizeRatio="4"
                        :spacing="0.5"
                        :animationDuration="0"
                ></vue-word-cloud>
            </DataPanel>
            <DataPanel
                title="Most used emojis"
                :class="{locked: locked, 'loading-animation': loaded}"
                type="most-used-emojis"
                :links="[]"
                :account="instagramName"
                :uuid="uuid"
                :count="emoji.length"
                :download="download">
            <vue-word-cloud
                    class="fixed-word-cloud"
                    :words="emoji"
                    :color="color"
                    font-family="Baloo Bhaijaan"
                    :fontSizeRatio="4"
                    :spacing="0.5"
                    :animationDuration="0"
            ></vue-word-cloud>
        </DataPanel>

        </div>
        <div class="data-panel-grid">
            <DataPanel
                    title="Most famous commenters"
                    :class="{locked: locked, 'loading-animation': loaded}"
                    type="most-famous-commenters"
                    :links="[]"
                    :account="instagramName"
                    :uuid="uuid"
                    :count="mostFamousCommenters.length"
                    :download="download">
                <Accounts
                    type="biggest"
                    :accounts="mostFamousCommenters"
                />
            </DataPanel>
            <DataPanel
                    title="Top positive commenters"
                    :class="{locked: locked, 'loading-animation': loaded}"
                    type="top-positive-commenters"
                    :links="[]"
                    :account="instagramName"
                    :uuid="uuid"
                    :count="topPositiveCommenters.length"
                    :download="download">
                <Accounts
                        type="sentiment"
                        :accounts="topPositiveCommenters"
                />
            </DataPanel>
        </div>
        <Posts
            :locked="locked"
            :posts="embedPosts"
        />

        <div v-if="bannerVisible" class="reports-banner-wrapper form-inline">
            <div class="reports-banner">
            <div class="content">
                <i class="fa fa-bar-chart"></i> <font-awesome-icon :icon="['far', 'chart-bar']"/> Get a report like this for your Instagram account: &nbsp;&nbsp;&nbsp;
                <instagram-modal v-if="info.loggedIn" btn-variant="light" btn-text="Create report"/>
                <b-btn v-if="!info.loggedIn" variant="light" to="/login" >Sign in</b-btn>
                <b-btn-close class="close-button" @click="closeBanner"></b-btn-close>
            </div>
            </div>
        </div>
    </b-container>
</template>

<script>
    import moment from 'moment'
    import lsq from 'least-squares'
    import _ from 'lodash'
    import {api} from '../../_services'
    import TotalBarChart from './TotalBarChart'
    import AverageBarChart from './AverageBarChart'
    import DataPanel from './DataPanel'
    import DominantColor from './DominantColor'
    import Heatmap from './Heatmap'
    import SentimentHistory from './SentimentHistory'
    import Histogram from './Histogram'
    import Accounts from './Accounts'
    import Posts from './Posts'
    import InstagramModal from './InstagramModal'
    import Twemoji from 'twemoji'
    import Sentiment from 'sentiment'
    import Chance from 'chance'
    import BookmarkButton from '../BookmarkButton'
    import VueWordCloud from 'vuewordcloud'
    import XLSX from 'xlsx'
    import { mapState } from 'vuex'

    const chance = new Chance()

    export default {
        name: 'Instagram',
        components: {
            Histogram,
            AverageBarChart,
            TotalBarChart,
            DataPanel,
            DominantColor,
            Heatmap,
            SentimentHistory,
            Accounts,
            BookmarkButton,
            Posts,
            InstagramModal,
            VueWordCloud
        },
        data () {
            return {
                uuid: null,
                loaded: false,
                locked: false,
                instagramName: '',
                followers: 0,
                posts: 0,
                comments: [],
                startAt: null,
                endAt: null,
                precision: 2,
                data: [],
                bannerVisible: true
            }
        },
        created () {
            this.uuid = this.$route.params.uuid
        },
        mounted () {
            this.loaded = true
            api.getInstagramReport(this.$route.params.uuid)
                .then(r => {
                    this.processData(r.data.data)
                    this.loaded = false
                })
                .catch(err => {
                    if (err.response && err.response.status === 401) {
                        this.$router.push('/login')
                        return
                    }
                    // todo show error message
                })
        },
        computed: {
             ...mapState('account', ['info']),
            commentsCount() {
                return this.comments.length
            },
            interval () {
                if (this.startAt == null || this.endAt == null) {
                    return []
                }
                const interval = []
                const start = this.startAt.clone()
                while(start <= this.endAt) {
                    interval.push(start.clone())
                    start.add(1, 'day')
                }
                return interval
            },
            table () {
                if (!this.data) {
                    return []
                }
                return _.slice(_.cloneDeep(this.data).map(x => {
                    delete x.hashtagSuggestions
                    delete x.userId
                    return x
                }), 0, 5)
            },
            commenters () {
                const commenters = {}
                this.comments.forEach(c => {
                    if (!_.isObject(commenters[c['username']])) {
                        commenters[c['username']] = {
                            username: c['username'],
                            full_name: c['full_name'],
                            profile_picture: c['profile_picture'],
                            followers: c['followers'],
                            posts: c['posts'],
                            comments: 0,
                            sentiment: []
                        }
                    }
                    commenters[c['username']].comments++
                    commenters[c['username']].sentiment.push(c['sentiment'])
                })

                return this.convertToArray(commenters).map(x => {
                    x.sentiment = this.avgArray(x.sentiment.filter(x => x != null))
                    return x
                })
            },
            tableCommenters () {
                if (!this.commenters) {
                    return []
                }
                return _.slice(_.cloneDeep(this.commenters).map(x => {
                    return x
                }), 0, 5)
            },
            postsCount() {
                return this.data ? this.data.length : 0
            },
            activity () {
                if (!this.data) {
                    return []
                }
                const activity = this.initArray(2)

                this.data.forEach(i => {
                    const key = moment(i['timestamp']).format('MM/DD/YYYY')
                    activity[key][1]++
                })
                return this.convertToArray(activity)
            },
            commentsChart () {
                if (!this.data) {
                    return []
                }
                const comments = this.initArray(3)

                this.comments.forEach(i => {
                    const key = moment(i['timestamp']).format('MM/DD/YYYY')
                    if (comments[key][2] === 0) {
                        comments[key][2] = []
                    }
                    comments[key][1]++
                    comments[key][2].push(i['sentiment'])
                })
                return this.convertToArray(comments)
            },
            uniqueCommentersChart () {
                if (!this.data) {
                    return []
                }
                const comments = this.initArray(2)
                const saved = {}
                this.comments.forEach(i => {
                    const key = moment(i['timestamp']).format('MM/DD/YYYY')
                    if (!Array.isArray(saved[key])) {
                        saved[key] = []
                    }
                    if (saved[key].indexOf(i['username']) === -1) {
                        saved[key].push(i['username'])
                        comments[key][1]++
                    }
                })
                return this.convertToArray(comments)
            },
            trendPostingActivity () {
                return this.growParam(this.activity, false)
            },
            impressions () {
                if (!this.data) {
                    return []
                }
                const impressions =  this.initArray(3)
                this.data.forEach(i => {
                    const key = moment(i['timestamp']).format('MM/DD/YYYY')
                    impressions[key][1] += i['impressions']
                    impressions[key][2]++
                })
                return this.convertToArray(impressions)
            },
            growsTotalImpressions () {
                return this.growParam(this.impressions, false)
            },
            growsAverageImpressions () {
                return this.growParam(this.impressions, true)
            },
            growsComments () {
                return this.growParam(this.commentsChart, false)
            },
            growsCommenters () {
                return this.growParam(this.uniqueCommentersChart, false)
            },
            engagements () {
                const engagemenent =  this.initArray(3)
                this.data.forEach(i => {
                    const key = moment(i['timestamp']).format('MM/DD/YYYY')
                    engagemenent[key][1] += i['engagement']
                    engagemenent[key][2]++
                })
                return this.convertToArray(engagemenent)
            },
            growsTotalEngagement () {
                return this.growParam(this.engagements, false)
            },
            growsAverageEngagement () {
                return this.growParam(this.engagements, true)
            },
            avgImpressions () {
                return this.avg(this.impressions)
            },
            avgEngagement () {
                return this.avg(this.engagements)
            },
            hashtags () {
                let hashtags = {};
                this.data.forEach(i => {
                    Array.from(new Set(i.hashtagsInCommentsByOwner.concat(i.hashtagsInCaption))).map(x => x.toLowerCase()).forEach(h => {
                        if (!hashtags[h]) {
                            hashtags[h] = {
                                hashtag: h,
                                count: 1,
                                impressions: [i['impressions']]
                            }
                        } else {
                            hashtags[h]['count']++;
                            hashtags[h]['impressions'].push(i['impressions']);
                        }
                    })

                })
                const avg = this.avgImpressions
                hashtags = this.convertToArray(hashtags).map(x => {
                    let count = x.impressions.length
                    return {
                        hashtag: x.hashtag,
                        used: x.count,
                        impressions: this.round(((x.impressions.reduce((a, b) => a + b) * 100 / count) / avg) - 100)
                    }
                })
                return _.slice(
                    _.sortBy(_.slice(_.sortBy(hashtags, (x) => -x.used), 0, 30), (x) => -x.impressions)
                    , 0, 10)
                    .filter(x => x.impressions > 0)
            },
            newHashtags () {
                const skip = []
                let hashtags = {};
                this.data.forEach(i => {
                    skip.push(...i.hashtagsInCaption)
                    skip.push(...i.hashtagsInCommentsByOwner)
                    skip.push(...i.hashtagsInCommentsByOthers)
                })
                this.data.forEach(i => {
                    i.hashtagSuggestions.map(x => x.hashtag.toLowerCase()).forEach(h => {
                        if (skip.indexOf(h) > -1) {
                            return
                        }
                        if (!hashtags[h]) {
                            hashtags[h] = {
                                hashtag: h,
                                count: 1,
                            }
                        } else {
                            hashtags[h]['count']++;
                        }
                    })

                })
                return _.slice(_.sortBy(hashtags, (x) => -x.count), 0, 10).map(x => {
                    delete x.count
                    return x
                });
            },
            engagementByType () {
                const image = {
                    type: 'Photo',
                    posted: 0,
                    engagement:0
                }
                const video = {
                    type: 'Video',
                    posted: 0,
                    engagement:0
                }
                const carousel = {
                    type: 'Carousel',
                    posted: 0,
                    engagement:0
                }


                this.data.forEach(i => {
                    switch(i['mediaType']) {
                        case 'IMAGE':
                            image.posted++
                            image.engagement += i['engagement']
                            break
                        case 'VIDEO':
                            video.posted++
                            video.engagement += i['engagement']
                            break
                        case 'CAROUSEL_ALBUM':
                            carousel.posted++
                            carousel.engagement += i['engagement']
                            break
                    }
                })
                video.engagement = video.posted > 0 ? Math.round(video.engagement * 100 / video.posted) / 100 : 0
                carousel.engagement = carousel.posted > 0 ? Math.round(carousel.engagement * 100 / carousel.posted) / 100 : 0
                image.engagement = image.posted > 0 ? Math.round(image.engagement * 100 / image.posted) / 100 : 0
                return _.sortBy([
                    video,
                    carousel,
                    image
                ], (x) => -x.engagement)
            },
            mostEngagingCaptions () {
                const emoji = {
                    type: 'emoji',
                    posted: 0,
                    engagement:0
                }
                const hashtag = {
                    type: 'hashtag',
                    posted: 0,
                    engagement:0
                }
                const question = {
                    type: 'question',
                    posted: 0,
                    engagement:0
                }

                this.data.forEach(i => {
                    if (i.captionWithQuestion) {
                        question.posted++
                        question.engagement += i['engagement']
                    }
                    if (i.captionWithHashtags) {
                        hashtag.posted++
                        hashtag.engagement += i['engagement']
                    }
                    if (i.captionWithEmoji) {
                        emoji.posted++
                        emoji.engagement += i['engagement']
                    }
                })

                const avg = this.avgEngagement

                question.grow = this.round(((question.engagement * 100 / question.posted) / avg) - 100) || 0
                hashtag.grow = this.round(((hashtag.engagement * 100 / hashtag.posted) / avg) - 100) || 0
                emoji.grow = this.round(((emoji.engagement * 100 / emoji.posted) / avg) - 100) || 0
                return {
                    question,
                    hashtag,
                    emoji
                }
            },
            mostEngagingContent () {
                if (!this.data) {
                    return  {}
                }
                const objects = {}
                this.data.forEach(i => {
                    for (let x of Array.from(i.imageObjects)) {
                        if (!objects[x]) {
                            objects[x] = {
                                object: x,
                                posted: 0,
                                engagement: 0,
                                images: [],
                                dupl: []
                            }
                        }
                        const url = i.permalink + 'media?size=m'// i.mediaType === 'VIDEO' ? i.thumbnail : i.mediaUrl
                        objects[x]['posted']++
                        objects[x]['engagement'] += i['engagement']
                        objects[x]['images'].push(url)
                    }
                })

                const avg = this.avgEngagement

                const result = this.convertToArray(objects).map(x => {
                    x['avgEngagement'] = this.round(x['posted'] === 0 ? 0 : x['engagement'] / x['posted'])
                    x['trend'] = this.round(x['posted'] === 0 ? 0 : (x['avgEngagement'] * 100 / avg) - 100)
                    return x
                })
                const used = []
                let start = 1
                const imagesResult = _.slice(_.sortBy(_.slice(_.sortBy(result, (x) => -x.posted), 0, 30), (x) => -x.trend), 0, 6)
                    .map(item => {
                        const images = []
                        const dup = []
                        item.images.forEach(i => {
                            if (used.indexOf(i) > -1) {
                                dup.push(i)
                            } else {
                                images.push(i)
                            }
                            used.push(i)
                        })
                        item.images = images
                        item.dup = dup
                        return item
                    }).map(item => {
                        if (item.images.length < 3) {
                            for (let i = 0; i <= 3 - item.images.length; i++) {
                                if (item.dup.length === 0) {
                                    break
                                }
                                let index = (start++ * 3) % item.dup.length
                                item.images.push(...item.dup.splice(index, 1))
                            }
                        }
                        delete item['dup']
                        return item
                    })
                return imagesResult.filter(x => x.engagement > 0)
            },
            engagementByColors() {
                const engagemenent =  {}
                this.data.forEach(i => {
                    if (i.colorPalette.length > 0) {
                        const hues = i.colorPalette.map(x => this.getHue(x))
                        const color = Math.round(hues.reduce((a, b) => a + b) / hues.length)
                        const key = Math.round(hues.reduce((a, b) => a + b) / (hues.length * 8))
                        if (!engagemenent[key]) {
                            engagemenent[key] = [color, 0, 0]
                        }
                        engagemenent[key][1] += i['engagement']
                        engagemenent[key][2]++
                    }
                })
                return _.sortBy(this.convertToArray(engagemenent), x => - (x[0]))
            },
            hashtagCountEffectOnEngagement () {
                const engagemenent =  {}
                this.data.forEach(i => {
                    const hashtagsCount = i.hashtagsInCaption.length + i.hashtagsInCommentsByOwner.length + i.hashtagsInCommentsByOthers.length
                    const key = hashtagsCount === 0 ? -1 : Math.floor((hashtagsCount - 1) / 5)
                    if (!engagemenent[key]) {
                        const desc = key === -1 ? 0 : (key * 5 + 1) + '-' + ((key + 1) * 5)
                        engagemenent[key] = [desc, 0, 0, key, []]
                    }
                    engagemenent[key][1] += i['engagement']
                    engagemenent[key][2]++
                    engagemenent[key][4].push(hashtagsCount)

                })
                return _.sortBy(this.convertToArray(engagemenent), x => x[3])
            },
            hashtagCountEffectOnImpressions () {
                const engagemenent =  {}
                this.data.forEach(i => {
                    const hashtagsCount = i.hashtagsInCaption.length + i.hashtagsInCommentsByOwner.length + i.hashtagsInCommentsByOthers.length
                    const key = hashtagsCount === 0 ? -1 : Math.floor((hashtagsCount - 1) / 5)
                    if (!engagemenent[key]) {
                        const desc = key === -1 ? 0 : (key * 5 + 1) + '-' + ((key + 1) * 5)
                        engagemenent[key] = [desc, 0, 0, key, []]
                    }
                    engagemenent[key][1] += i['impressions']
                    engagemenent[key][2]++
                    engagemenent[key][4].push(hashtagsCount)

                })
                return _.sortBy(this.convertToArray(engagemenent), x => x[3])
            },
            mostEngagingTimesToPost () {
                if (!this.data) {
                    return null
                }
                const matrix = [];
                for(let i = 0; i < 7; i++) {
                    matrix[i] = [];
                    for(let j = 0; j < 24; j++) {
                        matrix[i][j] = null // Math.floor(Math.random() * 100);
                    }
                }
                this.data.forEach(i => {
                    const date = moment(i.timestamp) // format('e') day of week format('H')

                    const x = date.format('H')
                    const y = date.format('e')
                    matrix[y][x] += i.engagement
                })
                return {
                    data: matrix,
                    labels: {
                        x: ['1a', '2a', '3a', '4a', '5a', '6a', '7a', '8a', '9a', '10a', '11a', '12a', '1p', '2p', '3p', '4p', '5p', '6p', '7p', '8p', '9p', '10p', '11p', '12p'],
                        y: ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su']
                    }
                }
            },
            sentimentHistory () {
                const data = {
                    labels: [],
                    positive: [],
                    count_positive: [],
                    negative: [],
                    count_negative: []
                }

                this.commentsChart.forEach(x => {
                    data.labels.push(x[0])
                    const positive = x[2] === 0 ? [] : x[2].filter(x => x > 0)
                    const negative = x[2] === 0 ? [] : x[2].filter(x => x < 0)

                    data.positive.push(this.avgArray(positive))
                    data.count_positive.push(positive.length)

                    data.negative.push(this.avgArray(negative))
                    data.count_negative.push(negative.length)

                })
                return data
            },
            sentimentHistoryCount () {
                let total = 0
                this.commentsChart.forEach(x => {
                    if(x[2] === 0 ? [] : x[2].filter(x => x !== 0).length > 0) {
                        total++
                    }
                })
                return total
            },
            commentsHistogram () {
                const histogram = [
                    [-1, 0],
                    [-0.2, 0],
                    [-0.4, 0],
                    [-0.6, 0],
                    [-0.8, 0],
                    [0, 0],
                    [0.2, 0],
                    [0.4, 0],
                    [0.6, 0],
                    [0.8, 0],
                    [1, 0]
                ]
                const rules = [
                    [0, -1, -0.9],
                    [1, -0.9, -0.7],
                    [2, -0.7, -0.5],
                    [3, -0.5, -0.3],
                    [4, -0.3, -0.1],
                    [5, -0.1, 0.1],
                    [6, 0.1, 0.3],
                    [7, 0.3, 0.5],
                    [8, 0.5, 0.7],
                    [9, 0.7, 0.9],
                    [10, 0.9, 1],
                ]
                this.comments.forEach(c => {
                    const s = c['sentiment']
                    if (s == null) {
                        return
                    }
                    rules.forEach(r => {
                        if (s >= r[1]  && s <= r[2]) {
                            histogram[r[0]][1]++
                        }
                    })
                })
                return histogram
            },
            emoji () {
                if (this.data) {
                    const emojis = {}
                    this.comments.forEach(e => {
                        Twemoji.replace(e.comment, (x) => {
                            if (!_.isObject(emojis[x])) {
                                emojis[x] = {
                                    e: x,
                                    count: 0
                                }
                            }
                            emojis[x].count ++
                        })
                    })
                    return this.convertToArray(emojis).map(x => [x.e, x.count])
                }
                return []
            },
            adjectives () {
                const positive = {}
                const negative = {}
                if (this.data) {
                    const sentiment = new Sentiment()
                    this.comments.forEach(e => {
                        const result = sentiment.analyze(e.comment);
                        result.positive.forEach(x => {
                            if (!_.isObject(positive[x])) {
                                positive[x] = {
                                    e: x,
                                    count: 0
                                }
                            }
                            positive[x].count ++
                        })
                        result.negative.forEach(x => {
                            if (!_.isObject(negative[x])) {
                                negative[x] = {
                                    e: x,
                                    count: 0
                                }
                            }
                            negative[x].count ++
                        })
                    })
                }
                return {
                    positive: this.convertToArray(positive).map(x => [x.e, x.count]),
                    negative: this.convertToArray(negative).map(x => [x.e, x.count])
                }
            },
            positiveAdjectives () {
                return this.adjectives.positive
            },
            negativeAdjectives () {
                return this.adjectives.negative
            },
            embedPosts () {
                const result = {
                    'positive': [], //['https://www.instagram.com/p/B1H9cFPnF-H/'],
                    'negative': [],
                    'engagement': [],
                    'impressions': [],
                    'likes': [],
                    'comments': []
                }
                if (this.data) {
                    result.engagement = _.sortBy(_.cloneDeep(this.data), x => -x.engagement)
                        .slice(0, 12)
                        .map(x => x.permalink)
                    result.impressions = _.sortBy(_.cloneDeep(this.data), x => -x.impressions)
                        .slice(0, 12)
                        .map(x => x.permalink)
                    result.likes = _.sortBy(_.cloneDeep(this.data), x => -x.likesCount)
                        .slice(0, 12)
                        .map(x => x.permalink)
                    result.comments = _.sortBy(_.cloneDeep(this.data), x => -x.commentsCount)
                        .slice(0, 12)
                        .map(x => x.permalink)
                    let posts = {}
                    this.comments.forEach(x => {
                        if(!_.isObject(posts[x.post_id])) {
                            posts[x.post_id] = {
                                permalink: x.permalink,
                                sentiment: []
                            }
                        }
                        posts[x.post_id].sentiment.push(x.sentiment)
                    })
                    posts = this.convertToArray(posts).map(x => {
                        x.sentiment = this.avgArray(x.sentiment)
                        return x;
                    })
                    result.positive = _.sortBy(posts.filter(x => x.sentiment > 0), x => -x.sentiment)
                        .map(x => x.permalink)
                        .slice(0, 12)
                    result.negative = _.sortBy(posts.filter(x => x.sentiment < 0), x => x.sentiment)
                        .map(x => x.permalink)
                        .slice(0, 12)
                }
                return result
            },
            mostFamousCommenters () {
                return _.sortBy(this.commenters.map(x => {
                    return {
                        profile_picture: x.profile_picture,
                        user_name: x.username,
                        comments: x.comments,
                        followers: x.followers
                    }
                }), x => -x.followers).slice(0, 5)
            },
            topPositiveCommenters () {
                return _.orderBy(this.commenters.filter(x => x.sentiment > 0), x => -x.sentiment)
                    .map(x => {
                        return {
                            profile_picture: x.profile_picture,
                            user_name: x.username,
                            comments: x.comments,
                            sentiment: x.sentiment
                        }
                    }).slice(0, 5)
            },
            topNegativeCommenters () {
                return _.orderBy(this.commenters.filter(x => x.sentiment < 0), x => x.sentiment)
                    .map(x => {
                        return {
                            profile_picture: x.profile_picture,
                            user_name: x.username,
                            comments: x.comments,
                            sentiment: x.sentiment
                        }
                    }).slice(0, 5)
            },
            mostEngagingTimesToPostCount () {
                if (!this.mostEngagingTimesToPost) {
                    return 0
                }
                let sum = 0
                for (let x of this.mostEngagingTimesToPost.data) {
                    for (let y of x) {
                        if (y > 0) {
                            sum++
                        }
                    }
                }
                return sum
            }
        },
        methods: {
            processData(report) {
                this.startAt = moment.utc(report.start_at, 'YYYY-MM-DD')
                this.endAt = moment.utc(report.end_at, 'YYYY-MM-DD')
                this.data = report.data.posts
                this.instagramName = report.data.user_name
                this.comments = report.data.comments
                this.followers = report.followers
            },
            convertToArray (object) {
                const arr = []
                for (let i in object) {
                    arr.push(object[i])
                }
                return arr
            },
            growParam (data, avg = false) {
                const params = {}
                let i = 0
                const X = data.map(() => i++)
                if (avg) {
                    lsq(X, data.map(x => x[2] === 0 ? 0 : x[1]/x[2]), params)
                } else {
                    lsq(X, data.map(x => x[1]), params)
                }

                return this.round(params.m)
            },
            initArray(size) {
                const res = {}
                this.interval.forEach(i => {
                    const key = i.format('MM/DD/YYYY')
                    const item = [key]
                    for (let i = 0; i < size - 1; i++) {
                        item.push(0)
                    }
                    res[key] = item
                })
                return res
            },
            avg (collection) {
                let sum = 0
                let count = 0
                for (let x of collection) {
                    if (x[2] > 0) {
                        sum += x[1]
                        count += x[2]
                    }
                }
                return count > 0 ? this.round(sum / count) : 0
            },
            avgArray (array) {
                if (array.length === 0) {
                    return null
                }
                const sum = array.reduce((a, b) => a + b)
                return this.round(sum / array.length)
            },
            avgScalar (sum, length) {
                return length === 0 ? 0 : this.round(sum / length)
            },
            round (number, precision = null) {
                const p = precision > 0 ? precision : Math.floor(2 - Math.log10(Math.abs(Math.floor(number) - number)))
                const x = Math.pow(10, p <= 2 ? 2 : p)
                return Math.round(number * x) / x
            },
            getHue (rgb) {
                return this.rgb2hsl(rgb[0], rgb[1], rgb[2])[0]
            },
            rgb2hsl (r, g, b) {
                // Make r, g, and b fractions of 1
                r /= 255
                g /= 255
                b /= 255

                // Find greatest and smallest channel values
                let cmin = Math.min(r,g,b),
                    cmax = Math.max(r,g,b),
                    delta = cmax - cmin,
                    h = 0,
                    s = 0,
                    l = 0

                // Calculate hue
                // No difference
                if (delta === 0)
                    h = 0;
                // Red is max
                else if (cmax === r)
                    h = ((g - b) / delta) % 6;
                // Green is max
                else if (cmax === g)
                    h = (b - r) / delta + 2;
                // Blue is max
                else
                    h = (r - g) / delta + 4;

                h = Math.round(h * 60);

                // Make negative hues positive behind 360°
                if (h < 0)
                    h += 360;

                l = (cmax + cmin) / 2;

                // Calculate saturation
                s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1))

                // Multiply l and s by 100
                s = +(s * 100).toFixed(1);
                l = +(l * 100).toFixed(1);

                // return "hsl(" + h + "," + s + "%," + l + "%)"
                return [h, s, l]
            },
            /**
             * Converts an HSL color value to RGB. Conversion formula
             * adapted from http://en.wikipedia.org/wiki/HSL_color_space.
             * Assumes h, s, and l are contained in the set [0, 1] and
             * returns r, g, and b in the set [0, 255].
             *
             * @param   {number}  h       The hue
             * @param   {number}  s       The saturation
             * @param   {number}  l       The lightness
             * @return  {Array}           The RGB representation
             */
            hslToRgb (h, s, l) {
                var r, g, b;

                if(s === 0){
                    r = g = b = l; // achromatic
                }else{
                    var hue2rgb = function hue2rgb(p, q, t){
                        if(t < 0) t += 1;
                        if(t > 1) t -= 1;
                        if(t < 1/6) return p + (q - p) * 6 * t;
                        if(t < 1/2) return q;
                        if(t < 2/3) return p + (q - p) * (2/3 - t) * 6;
                        return p;
                    }

                    var q = l < 0.5 ? l * (1 + s) : l + s - l * s;
                    var p = 2 * l - q;
                    r = hue2rgb(p, q, h + 1/3);
                    g = hue2rgb(p, q, h);
                    b = hue2rgb(p, q, h - 1/3);
                }

                return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
            },
            color () {
                const colors = ['#ffd077', '#3bc4c7', '#3a9eea', '#ff4e69', '#461e47']
                return chance.pickone(colors)
            },
            download (dname, type) {
                const name = (dname + '-' + this.uuid) + '.' + (type === 'csv' ? 'csv' : 'xlsx')
                const data = this.getData(dname)
                const wb = XLSX.utils.book_new()
                const ws = XLSX.utils.json_to_sheet(data)
                XLSX.utils.book_append_sheet(wb, ws, dname.substr(0, 31))
                XLSX.writeFile(wb, name)
            },
            getData (dname) {
                switch (dname) {
                case 'all-posts':
                    return _.cloneDeep(this.data).map(x => {
                        x.hashtagsInCaption = x.hashtagsInCaption.map(h => `#${h}`).join(', ')
                        x.hashtagsInCommentsByOwner = x.hashtagsInCommentsByOwner.map(h => `#${h}`).join(', ')
                        x.hashtagsInCommentsByOthers = x.hashtagsInCommentsByOthers.map(h => `#${h}`).join(', ')
                        x.imageObjects = _.isArray(x.imageObjects) ? x.imageObjects.join(', ') : ''
                        x.colorPalette = x.colorPalette.map(x=> '#' + x[0].toString(16) + x[1].toString(16) + x[2].toString(16))
                            .join(' ')
                        x.hashtagSuggestions = x.hashtagSuggestions.map(x => '#' + x.hashtag).join(', ')
                        return x
                    })
                case 'all-comments':
                    return _.cloneDeep(this.comments).map(x => {
                        delete x.posts
                        return x
                    })
                case 'post-activity':
                    return this.activity.map(x => {
                        return {
                            date: x[0],
                            posts: x[1]
                        }
                    })
                case 'total-impressions':
                    return this.impressions.map(x => {
                        return {
                            date: x[0],
                            impressions: x[1]
                        }
                    })
                case 'average-impressions-per-posts':
                    return this.impressions.map(x => {
                        return {
                            date: x[0],
                            impressions: x[1],
                            posts: x[2],
                            average: this.avgScalar(x[1], x[2]),
                        }
                    })
                case 'most-effective-hashtags':
                    return this.hashtags.map(x => {
                        return {
                            hashtag: x.hashtag,
                            used: x.used,
                            'impressions boost %': x.impressions
                        }
                    })
                case 'new-hashtags':
                    return this.newHashtags
                case 'hashtag-count-effect-on-impressions':
                    return this.hashtagCountEffectOnImpressions.map(x => {
                        return {
                            'hashtags count': x[0],
                            'average impressions': this.avgScalar(x[1], x[2]),
                        }
                    })
                case 'hashtag-count-effect-on-engagement':
                    return this.hashtagCountEffectOnEngagement.map(x => {
                        return {
                            'hashtags count': x[0],
                            'average engagement': this.avgScalar(x[1], x[2]),
                        }
                    })
                case 'total-engagement':
                    return this.engagements.map(x => {
                        return {
                            date: x[0],
                            engagement: x[1]
                        }
                    })
                case 'average-engagement-per-post':
                    return this.engagements.map(x => {
                        return {
                            date: x[0],
                            'average engagement': this.avgScalar(x[1], x[2]),
                        }
                    })
                case 'most-engaging-content-of-the-photos':
                    return this.mostEngagingContent.map(x => {
                        return {
                            object: x.object,
                            posted: x.posted,
                            engagement: x.engagement,
                            'average engagement': x.avgEngagement,
                            'boost %': x.trend
                        }
                    })
                case 'most-engaging-times-to-post':
                    let data = []
                    let days = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su']
                    let items = this.mostEngagingTimesToPost.data
                    for (let x in items) {
                        const row = { '': days[x] }
                        for (let y in items[x]) {
                            row[(y%12 + 1) + (y >= 12 ? 'p' : 'a')] = items[x][y]
                        }
                        data.push(row)
                    }
                    return data
                case 'engagement-by-colors-in-the-photo':
                    return this.engagementByColors.map(x => {
                        return {
                            color: '#' + this.hslToRgb(x[0] / 360, 0.80, 0.61).map(x => x.toString(16)).join(''),
                            'average engagement': x[1],
                            posts: x[2]
                        }
                    })
                case 'engagement-by-media-type':
                    return this.engagementByType.map(x => {
                        return {
                            type: x.type,
                            posted: x.posted,
                            'average engagement': x.engagement
                        }
                    })
                case 'caption-analytics':
                    const captions = this.mostEngagingCaptions
                    return [
                        {
                            type: "emojis",
                            'engagement boost': captions.emoji.grow
                        },{
                            type: "hashtags",
                            'engagement boost': captions.hashtag.grow
                        },{
                            type: "questions",
                            'engagement boost': captions.question.grow
                        },
                    ]
                case 'comments-chart':
                    return this.commentsChart.map(x => {
                        return {
                            date: x[0],
                            comments: x[1]
                        }
                    })
                case 'unique-commenters':
                    return this.uniqueCommentersChart.map(x => {
                        return {
                            date: x[0],
                            'unique commenters': x[1]
                        }
                    })
                case 'sentiment-timeline':
                    const sentimentTimeline = []
                    this.commentsChart.forEach(x => {
                        const positive = x[2] === 0 ? [] : x[2].filter(x => x > 0)
                        const negative = x[2] === 0 ? [] : x[2].filter(x => x < 0)
                        sentimentTimeline.push({
                            date: x[0],
                            'positive sentiment': this.avgArray(positive),
                            'negative sentiment': this.avgArray(negative),
                        })
                    })
                    return sentimentTimeline
                    // return this.sentimentHistory
                case 'positive-adjectives':
                    return this.positiveAdjectives.map(x => {
                        return {
                            adjectives: x[0],
                            count: x[1]
                        }
                    })
                case 'negative-adjectives':
                    return this.negativeAdjectives.map(x => {
                        return {
                            adjectives: x[0],
                            count: x[1]
                        }
                    })
                case 'overall-comment-sentiment':
                    return this.commentsHistogram.map(x =>{
                        return {
                            sentiment: x[0],
                            count: x[1]
                        }
                    })
                case 'most-famous-commenters':
                    return this.mostFamousCommenters
                case 'most-used-emojis':
                    return this.emoji.map(x => {
                        return {
                            emoji: x[0],
                            count: x[1],
                        }
                    })
                case 'top-positive-commenters':
                    return this.topPositiveCommenters
                case 'top-negative-commenters':
                    return this.topNegativeCommenters
                default:
                    return []
                }
            },
            closeBanner() {
                this.bannerVisible = false;
            }
        }
    }
</script>
<style scoped lang="scss">
.data-panel-grid {
    display: grid;
    grid-column-gap: 10px;
    grid-template-columns: repeat(2, 1fr);
}
.symbol-grid {
    display: grid;
    grid-gap: 40px;
    grid-template-columns: repeat(3, 1fr);
    >div {
        text-align: center;
        .symbol {
            font-size: 30px;
            border-radius: 50%;
            display:inline-block;
            width: 70px;
            height: 70px;
            line-height: 72px;
            &.emoji {
                background-color:#ffdfa6;
                color:rgba(255,255,255,0.6);
            }
            &.hashtag {
                background-color:#f8d5ed;
                color:rgba(255,255,255,0.6);
            }
            &.question {
                background-color: #b6edf6;
                color: rgba(255,255,255,0.6);
            }
        }
        .number {
            font-size: 40px;
            color: silver;
        }
    }
}
.carousel-grid {
    display: grid;
    grid-column-gap: 0px;
    grid-template-columns: repeat(3, 1fr);
    >div {
        text-align: center;
        align-self: end;
        padding:5px;
        .number {
            font-size: 40px;
        }
    }
}

/deep/ .carousel-item>img {
    height: 150px !important;
    width: auto !important;
    margin-left: auto;
    margin-right: auto;
}
.heading-no-wrap {
    white-space: nowrap;
}

.fading:after {
    content: " ";
    height: 200px;
    width: 100%;
    background: linear-gradient(180deg,hsla(0,0%,100%,0) 0,#fff);
    filter: progid:DXImageTransform.Microsoft.gradient(startColorstr="#00ffffff",endColorstr="#ffffff",GradientType=0);
    position: absolute;
    bottom: 74px;
}

.profile-picture {
    border-radius: 50%;
    width: 18px ;
}

.fixed-word-cloud {
    height: 200px !important;
}
table.table {
    >tr>td {
        vertical-align: middle;
    }
}
.reports-banner-wrapper {
    position: fixed;
    bottom: 20px;
    left: 0px;
    padding: 0px 10px;
    width: 100%;
    z-index: 100;
    pointer-events: none;
    text-align: center;
    .reports-banner {
        background-color: $primary;
        color: white;
        border-radius: 4px;
        pointer-events: all;
        display: inline-block;
        margin-left:auto;
        margin-right: auto;
        box-shadow: $shadow-big;

        .content {
            position: relative;
            padding: 10px 35px 10px 20px;

            #hashtag {
                width: 160px;
                height: 37px;
            }
            .input-group {
                margin-top: 6px;
                margin-right: 5px;
            }
            .close-button {
                cursor: pointer;
                position: absolute;
                top: 0px;
                right: 5px;
                color: white;
            }
        }
    }
}
</style>
<style>
    .hidden_header {
        display: none;
    }
</style>
